import { Checkbox, FormControlLabel, Grid2, Typography } from '@mui/material';
import type { RouteComponentProps } from '@reach/router';
import type { FieldProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import { QRCodeSVG } from 'qrcode.react';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSearchParam } from 'react-use';
import * as Yup from 'yup';

import Alert from '@components/Alert/Alert';
import MfaMethodList from '@components/ConfigOptionList/MfaMethodList';
import { FormikCodeField, FormikTelField } from '@components/FormikFields';
import Popper from '@components/Popper/index';
import RoundedButton from '@components/RoundedButton/index';
import SkeletonLoader from '@components/SkeletonLoader';

import { isProduction } from '@constants/env';
import * as ROUTES from '@constants/routes';

import {
  useEnableMfa,
  useSendSmsMfaCode,
  useSendTotpMfaCode,
} from '@hooks/api/session';
import { useGetUserMfaSettings } from '@hooks/api/user';
import useCommonSchemas from '@hooks/schemas/useCommonSchemas';
import { useNavigate } from '@hooks/useNavigate';
import { useSnackbar } from '@hooks/useSnackbar';

import AuthLayout, { BackLink } from '@layouts/AuthLayout';

import { getQueryParams } from '@utils/url';

import { MfaMethod } from 'generated/types';

const messages = defineMessages({
  title: {
    id: 'setupMfaLogin.title',
    defaultMessage: 'Setup MFA',
  },
  subtitle: {
    id: 'setupMfaLogin.subtitle',
    defaultMessage: 'Multi-factor authentication is required in order to login',
  },
  moreInfo: {
    id: 'setupMfaLogin.moreInfo',
    defaultMessage:
      'When enabled, there will be two steps when logging into your account: Entering your username and password, followed by entering a code sent to your phone or third-party authenticator app.',
  },
  goBack: {
    id: 'GLOBAL.goBack',
    defaultMessage: 'Back to login',
  },
  verify: {
    id: 'GLOBAL.verify',
    defaultMessage: 'Verify',
  },
  codeRequired: {
    id: 'mfaLogin.codeRequired',
    defaultMessage: 'Authentication code is required',
  },
  error: {
    id: 'errorMessage.setupMfa',
    defaultMessage: 'An error occurred while setting up MFA.',
  },
  expiredCode: {
    id: 'GLOBAL.expiredCode',
    defaultMessage: 'Code expired.',
  },
  incorrectCode: {
    id: 'GLOBAL.incorrectCode',
    defaultMessage: 'Incorrect code.',
  },
  code: {
    id: 'mfaSettings.code',
    defaultMessage: 'code',
  },
  totpTitle1: {
    id: 'mfaSettings.totpTitle1',
    defaultMessage: 'Scan this barcode or enter this ',
  },
  totpTitle2: {
    id: 'mfaSettings.totpTitle2',
    defaultMessage: ' into your authenticator app',
  },
  enterCode: {
    id: 'mfaSettings.enterCode',
    defaultMessage: 'After receiving a six-digit code, enter the code below',
  },
  smsTitle: {
    id: 'mfaSettings.smsTitle',
    defaultMessage:
      'An authentication code will be sent to the phone number you provide',
  },
  phone: {
    id: 'GLOBAL.phone',
    defaultMessage: 'Phone Number',
  },
  smsCodeError: {
    id: 'mfaSettings.smsCodeError',
    defaultMessage:
      'An error occurred while attempting to send a verification code to your phone.',
  },
  send: {
    id: 'mfaSettingsPhone.send',
    defaultMessage: 'Send code',
  },
  emailNoPhoneWarningTitle: {
    id: 'mfaSettings.emailNoPhoneWarningTitle',
    defaultMessage: 'Password reset requires support',
  },
  emailNoPhoneWarningBody: {
    id: 'mfaSettings.emailNoPhoneWarningBody',
    defaultMessage:
      'If you use email to receive your sign-in code, you cannot also use it to reset your password.\n\nA phone number is not on file, which means you will need to contact support to reset your password. Add a phone number to your account or choose a different MFA method to reset it on your own.',
  },
  emailNoPhoneAcknowledge: {
    id: 'mfaSettings.emailNoPhoneAcknowledge',
    defaultMessage:
      "I understand I'll need to contact support to reset my password.",
  },
  phoneRecoveryInfo: {
    id: 'mfaSettings.phoneRecoveryInfo',
    defaultMessage:
      'Your phone number {phoneNumber} will be used for account recovery.',
  },
  enable: {
    id: 'GLOBAL.enable',
    defaultMessage: 'Enable',
  },
});

type FormValues = {
  code: string[];
  method?: MfaMethod | null;
  phone?: string;
};

const SetupMfaForm: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { phoneSchema } = useCommonSchemas();
  const { navigateRoute } = useNavigate();
  const [sendSmsMfaCode, sendSmsMfaCodeLoading] = useSendSmsMfaCode();
  const [
    sendTotpMfaCode,
    { data: sendTotpData, loading: sendTotpMfaCodeLoading },
  ] = useSendTotpMfaCode();
  const [enableMfa] = useEnableMfa();
  // Used as username in authenticator app
  const email = useSearchParam('email');
  const methods = JSON.parse(
    useSearchParam('MFAS_CAN_SETUP') ?? '[]'
  ) as MfaMethod[];

  const { data: mfaData } = useGetUserMfaSettings();
  const mfaResponse = mfaData?.getUserRecord.mfa;
  const existingPhone = mfaResponse?.phone_number ?? null;
  const existingPhoneVerified = Boolean(mfaResponse?.phoneVerified);

  const totpEnabled = methods.includes(MfaMethod.SOFTWARE_TOKEN_MFA);
  const smsEnabled = methods.includes(MfaMethod.SMS_MFA);
  const emailOtpEnabled = methods.includes(MfaMethod.EMAIL_OTP);

  const [acknowledgedNoPhone, setAcknowledgedNoPhone] = useState(false);

  const [popperOpen, setPopperOpen] = useState<boolean>(false);
  const handleClick = () => setPopperOpen(!popperOpen);
  const handleClose = () => setPopperOpen(false);

  const validationSchema: Yup.Schema<Partial<FormValues>> = Yup.object().shape({
    code: Yup.array().when('method', {
      is: MfaMethod.EMAIL_OTP,
      then: (schema) => schema.of(Yup.string().max(1)),
      otherwise: (schema) =>
        schema
          .of(
            Yup.string().max(1).required(formatMessage(messages.codeRequired))
          )
          .required(formatMessage(messages.codeRequired)),
    }),
    method: Yup.string()
      .oneOf([
        MfaMethod.SOFTWARE_TOKEN_MFA,
        MfaMethod.SMS_MFA,
        MfaMethod.EMAIL_OTP,
      ])
      .nullable(),
    phone: phoneSchema,
  });

  const initialValues: FormValues = {
    code: ['', '', '', '', '', ''],
    method: null,
    phone: '',
  };

  const issuer = `VITALL%20Engage${isProduction ? '' : '%20DEV'}`;
  const url = `otpauth://totp/${issuer}:${email}?secret=${sendTotpData?.sendTotpMfaCode.code}&issuer=${issuer}`;

  async function handleSubmit({ code, method }: FormValues) {
    const { redirect_uri } = getQueryParams();
    if (!method) {
      throw new Error('No method selected');
    }
    const { errors } = await enableMfa({
      input: {
        ...(method !== MfaMethod.EMAIL_OTP && { code: code.join('') }),
        method,
        preferredMethod: method,
      },
    });
    if (!errors) {
      navigateRoute(ROUTES.LOADING, { redirect_uri });
    }
    if (errors?.length) {
      const errorMessage = errors.at(0)?.message;

      const isCodeMismatch = errorMessage === 'Incorrect verification code';
      const isExpiredCode = errorMessage === 'Verification code has expired';
      const message = isCodeMismatch
        ? formatMessage(messages.incorrectCode)
        : isExpiredCode
          ? formatMessage(messages.expiredCode)
          : formatMessage(messages.error);
      enqueueSnackbar({
        message,
        variant: 'error',
      });
    }
  }

  return (
    <AuthLayout title={formatMessage(messages.title)}>
      <Typography textAlign="center" variant="body2" mb={1}>
        {formatMessage(messages.subtitle)}
      </Typography>
      {/* <Typography textAlign="center" variant="body2">
        {formatMessage(messages.moreInfo)}
      </Typography> */}

      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ errors, isSubmitting, setFieldValue, values }) => {
          const handleSendSmsCode = async () => {
            if (!values.phone) {
              throw new Error('No phone number or token');
            }
            const response = await sendSmsMfaCode({
              input: {
                phoneNumber: values.phone,
              },
            });
            if (!response.data?.sendSmsMfaCode.message) {
              enqueueSnackbar({
                message: formatMessage(messages.smsCodeError),
                variant: 'error',
              });
            }
          };
          const handleSendTotpCode = async () => {
            await sendTotpMfaCode();
          };

          const handleMfaMethodSelect = async (value: MfaMethod) => {
            if (value === MfaMethod.SOFTWARE_TOKEN_MFA) {
              await handleSendTotpCode();
            }
            setFieldValue('method', value);
            setAcknowledgedNoPhone(false);
          };

          return (
            <Form>
              <MfaMethodList
                config={{
                  showConfig: false,
                  smsDisabled: !smsEnabled,
                  totpDisabled: !totpEnabled,
                  emailDisabled: !emailOtpEnabled,
                }}
                extraContent={{
                  [MfaMethod.EMAIL_OTP]:
                    values.method === MfaMethod.EMAIL_OTP ? (
                      existingPhoneVerified ? (
                        <Typography
                          variant="caption"
                          color="success.main"
                          sx={{ px: 2, pb: 1 }}
                        >
                          {formatMessage(messages.phoneRecoveryInfo, {
                            phoneNumber: existingPhone,
                          })}
                        </Typography>
                      ) : (
                        <Alert
                          severity="warning"
                          title={formatMessage(
                            messages.emailNoPhoneWarningTitle
                          )}
                          sx={{ m: 1, mt: 0 }}
                        >
                          <Typography variant="caption" whiteSpace="pre-line">
                            {formatMessage(messages.emailNoPhoneWarningBody)}
                          </Typography>
                          <FormControlLabel
                            sx={{
                              mt: 1,
                              alignItems: 'flex-start',
                              ml: 0,
                              gap: 0.5,
                            }}
                            control={
                              <Checkbox
                                checked={acknowledgedNoPhone}
                                onChange={(e) =>
                                  setAcknowledgedNoPhone(e.target.checked)
                                }
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                                sx={{ p: 0, mt: '-1px' }}
                              />
                            }
                            label={
                              <Typography variant="caption" lineHeight={1.4}>
                                {formatMessage(
                                  messages.emailNoPhoneAcknowledge
                                )}
                              </Typography>
                            }
                          />
                        </Alert>
                      )
                    ) : undefined,
                }}
                method={values.method ?? ''}
                onClick={handleMfaMethodSelect}
              />

              <Grid2 container spacing={2}>
                {values.method === MfaMethod.SOFTWARE_TOKEN_MFA && (
                  <SkeletonLoader loading={sendTotpMfaCodeLoading}>
                    <Grid2 size={{ xs: 12 }}>
                      <Typography
                        component="span"
                        textAlign="center"
                        variant="caption"
                      >
                        {formatMessage(messages.totpTitle1)}
                        <Popper
                          buttonContent={
                            <Typography
                              component="span"
                              textAlign="center"
                              variant="caption"
                            >
                              {formatMessage(messages.code)}
                            </Typography>
                          }
                          copyContent
                          onClick={handleClick}
                          onClose={handleClose}
                          open={popperOpen}
                          popoverContent={
                            sendTotpData?.sendTotpMfaCode.code ?? ''
                          }
                          type="link"
                        />
                        {formatMessage(messages.totpTitle2)}
                      </Typography>
                    </Grid2>

                    <Grid2 sx={{ textAlign: 'center' }} size={{ xs: 12 }}>
                      <QRCodeSVG size={100} value={url} />
                    </Grid2>
                  </SkeletonLoader>
                )}
                {values.method === MfaMethod.SMS_MFA && (
                  <>
                    <Grid2 size={{ xs: 12 }}>
                      <Typography variant="caption">
                        {formatMessage(messages.smsTitle)}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 8 }}>
                      <FormikTelField
                        label={formatMessage(messages.phone)}
                        name="phone"
                        required
                      />
                    </Grid2>
                    <Grid2
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                      }}
                      size={{ xs: 4 }}
                    >
                      <RoundedButton
                        disabled={
                          Boolean(errors.phone) || sendSmsMfaCodeLoading
                        }
                        fontSize={15}
                        height={20}
                        onClick={handleSendSmsCode}
                      >
                        {formatMessage(messages.send)}
                      </RoundedButton>
                    </Grid2>
                  </>
                )}
              </Grid2>

              {values.method && values.method !== MfaMethod.EMAIL_OTP && (
                <>
                  <Typography mt={2} textAlign="center" variant="body2">
                    {formatMessage(messages.enterCode)}
                  </Typography>
                  <Field name="code">
                    {({ field }: FieldProps<string>) => (
                      <FormikCodeField
                        {...field}
                        codeLength={values.code.length}
                      />
                    )}
                  </Field>

                  <RoundedButton
                    disabled={isSubmitting}
                    height={40}
                    type="submit"
                  >
                    {formatMessage(messages.verify)}
                  </RoundedButton>
                </>
              )}

              {values.method === MfaMethod.EMAIL_OTP &&
                (existingPhoneVerified || acknowledgedNoPhone) && (
                  <RoundedButton
                    disabled={isSubmitting}
                    height={40}
                    type="submit"
                  >
                    {formatMessage(messages.enable)}
                  </RoundedButton>
                )}
            </Form>
          );
        }}
      </Formik>
      <BackLink to={ROUTES.LOGIN}>{formatMessage(messages.goBack)}</BackLink>
    </AuthLayout>
  );
};

export default SetupMfaForm;
